import { useCallback, useRef } from "react";
import { Moment } from "moment";
import { MiButton, MiButtonGroup } from "@miplatform/components";
import { DATE_TIME_FORMATS } from "@miplatform/constants";
import { ButtonVariants, Colors } from "@miplatform/types";
import { formatDate, getDate, isSameDay } from "@miplatform/utils";
import {
  mdiCalendarBlank,
  mdiCalendarMonth,
  mdiCalendarRefresh,
} from "@mdi/js";
import { Box } from "@mui/material";
import { DatePicker } from "../../../components/DatePicker/DatePicker";
import { AssignmentsUnassignedFilter } from "./AssignmentsCalendar.constants";
import { getAssignmentDate } from "./AssignmentsCalendar.util";
import { AssignmentsStackedField } from "./AssignmentsStackedField";

interface AssignmentsUnassignedActionsProps {
  date: Moment;
  unassignedFilter: AssignmentsUnassignedFilter;
  onUnassignedFilterChange: (filter: AssignmentsUnassignedFilter) => void;
  onDateChange: (date: Moment) => void;
}

export const AssignmentsUnassignedActions = ({
  date,
  unassignedFilter,
  onUnassignedFilterChange,
  onDateChange,
}: AssignmentsUnassignedActionsProps) => {
  const assignmentDate = useRef<Moment>(getAssignmentDate());
  const today = useRef<Moment>(getDate());
  const pickerColor = isSameDay(date, assignmentDate.current)
    ? Colors.Primary
    : Colors.Error;

  const handleAssignmentDateClick = useCallback(() => {
    onDateChange(assignmentDate.current);
  }, [onDateChange]);

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      flex={1}
      gap={2}
    >
      <Box pl={2}>
        <AssignmentsStackedField title="Today">
          {formatDate(today.current, DATE_TIME_FORMATS.FORMAT_ddd_MMM_D_YYYY)}
        </AssignmentsStackedField>
      </Box>
      <Box display="flex" alignItems="center" gap={2}>
        <DatePicker
          hideTodayButton
          date={date}
          color={pickerColor}
          setDate={onDateChange}
        />
        <MiButton
          size="small"
          variant={ButtonVariants.Contained}
          title="Assignment Date"
          startIcon={{
            path: mdiCalendarRefresh,
          }}
          onClick={handleAssignmentDateClick}
          disabled={isSameDay(date, assignmentDate.current)}
          disableRipple
          data-testid="assignment-date-button"
        />
      </Box>
      <MiButtonGroup
        condensed
        selected={unassignedFilter}
        data={[
          {
            label: "All",
            value: AssignmentsUnassignedFilter.All,
            icon: mdiCalendarMonth,
          },
          {
            label: "Assignment Date",
            value: AssignmentsUnassignedFilter.AssignmentDate,
            icon: mdiCalendarBlank,
          },
        ]}
        containerHeight={35}
        handleClick={(item: { value: AssignmentsUnassignedFilter }) =>
          onUnassignedFilterChange(item.value)
        }
      />
    </Box>
  );
};
